import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, X, Wrench, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { processImages } from '../utils/imageCompression';

const WorkExecutionManager = ({ report, onUpdate }) => {
  const { t } = useTranslation();
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Vendor assignment state
  const [vendorName, setVendorName] = useState('');
  const [vendorContact, setVendorContact] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  const [showPublicly, setShowPublicly] = useState(false);
  const [workStartDate, setWorkStartDate] = useState('');

  // Work update state
  const [updateDescription, setUpdateDescription] = useState('');
  const [updatePhotos, setUpdatePhotos] = useState([]);
  const [updateStatus, setUpdateStatus] = useState('in_progress');

  // Completion state
  const [finalPhotos, setFinalPhotos] = useState([]);
  const [completionNotes, setCompletionNotes] = useState('');

  const [processing, setProcessing] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const handlePhotoUpload = async (e, setter) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setCompressing(true);
    try {
      const compressedImages = await processImages(files);
      setter(prev => [...prev, ...compressedImages]);
      toast.success(`${files.length} photo(s) compressed`);
    } catch (error) {
      toast.error('Failed to process images');
    } finally {
      setCompressing(false);
    }
  };

  const removePhoto = (index, photos, setter) => {
    setter(photos.filter((_, i) => i !== index));
  };

  const handleAssignVendor = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/moderator/reports/${report.id}/assign-vendor`,
        {
          vendor_name: vendorName,
          vendor_contact: vendorContact,
          vendor_email: vendorEmail || null,
          show_publicly: showPublicly,
          work_start_date: workStartDate || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Vendor assigned successfully');
      setShowVendorModal(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign vendor');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/moderator/reports/${report.id}/work-update`,
        {
          description: updateDescription,
          photos: updatePhotos,
          status: updateStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Work update added');
      setShowUpdateModal(false);
      setUpdateDescription('');
      setUpdatePhotos([]);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add update');
    } finally {
      setProcessing(false);
    }
  };

  const handleCompleteWork = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/moderator/reports/${report.id}/complete-work`,
        {
          final_photos: finalPhotos,
          completion_notes: completionNotes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Work marked as completed');
      setShowCompleteModal(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete work');
    } finally {
      setProcessing(false);
    }
  };

  const toggleVendorVisibility = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/moderator/reports/${report.id}/vendor-visibility`,
        null,
        {
          params: { show_publicly: !report.vendor?.show_publicly },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Vendor visibility updated');
      onUpdate();
    } catch (err) {
      toast.error('Failed to update visibility');
    }
  };

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        {!report.vendor && (
          <Button onClick={() => setShowVendorModal(true)}>
            <Wrench size={16} className="mr-2" />
            {t('work.assignVendor')}
          </Button>
        )}

        {report.vendor && report.status !== 'completed' && (
          <>
            <Button onClick={() => setShowUpdateModal(true)} variant="outline">
              <Clock size={16} className="mr-2" />
              {t('work.addUpdate')}
            </Button>

            <Button onClick={() => setShowCompleteModal(true)} className="bg-green-600 hover:bg-green-700">
              <CheckCircle size={16} className="mr-2" />
              {t('work.completeWork')}
            </Button>
          </>
        )}
      </div>

      {/* Vendor Assignment Modal */}
      <Dialog open={showVendorModal} onOpenChange={setShowVendorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('work.assignVendor')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAssignVendor} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('work.vendorName')}</label>
              <Input
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('work.vendorContact')}</label>
              <Input
                value={vendorContact}
                onChange={(e) => setVendorContact(e.target.value)}
                placeholder="Phone number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('work.vendorEmail')}</label>
              <Input
                type="email"
                value={vendorEmail}
                onChange={(e) => setVendorEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('work.workStartDate')}</label>
              <Input
                type="date"
                value={workStartDate}
                onChange={(e) => setWorkStartDate(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showPublicly}
                onChange={(e) => setShowPublicly(e.target.checked)}
                className="w-4 h-4"
              />
              <label className="text-sm">{t('work.showPublicly')}</label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1" disabled={processing}>
                {processing ? t('mod.processing') : t('work.assign')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowVendorModal(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Work Update Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('work.addUpdate')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAddUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('work.updateDescription')}</label>
              <Textarea
                value={updateDescription}
                onChange={(e) => setUpdateDescription(e.target.value)}
                rows={4}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('work.status')}</label>
              <Select value={updateStatus} onValueChange={setUpdateStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="started">{t('work.started')}</SelectItem>
                  <SelectItem value="in_progress">{t('work.inProgress')}</SelectItem>
                  <SelectItem value="completed">{t('work.completed')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('work.uploadPhotos')}</label>
              {updatePhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {updatePhotos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img src={photo} alt="" className="w-full h-24 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx, updatePhotos, setUpdatePhotos)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="image-upload-zone cursor-pointer block">
                <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                <p className="text-gray-600 text-sm">{t('work.uploadPhotos')}</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handlePhotoUpload(e, setUpdatePhotos)}
                />
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1" disabled={processing}>
                {processing ? t('mod.processing') : t('work.addUpdateBtn')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowUpdateModal(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Work Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('work.completeWork')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCompleteWork} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('work.completionNotes')}</label>
              <Textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('work.finalPhotos')}</label>
              {finalPhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {finalPhotos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img src={photo} alt="" className="w-full h-24 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx, finalPhotos, setFinalPhotos)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="image-upload-zone cursor-pointer block">
                <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                <p className="text-gray-600 text-sm">{t('work.finalPhotos')}</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handlePhotoUpload(e, setFinalPhotos)}
                />
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" disabled={processing}>
                {processing ? t('mod.processing') : t('work.markComplete')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCompleteModal(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkExecutionManager;
